import { useState } from 'react'
import { Copy, Check, Share2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@components/ui/dialog'
import { Button } from '@components/ui/button'
import { notifyToast } from 'utils/responseUtils'
import { FaFacebookF, FaWhatsapp, FaInstagram, FaTelegramPlane, FaLinkedinIn, FaEnvelope } from 'react-icons/fa'
import { RiTwitterXLine } from 'react-icons/ri'
import { SiThreads } from 'react-icons/si'
import { FaYoutube } from 'react-icons/fa'
import { FaTiktok } from 'react-icons/fa6'

export const SOCIAL_MEDIA_PLATFORMS = [
  {
    name: 'Facebook',
    icon: FaFacebookF,
    color: '#1877f2',
    shareUrl: (url, title) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(title)}`,
    gradient: 'from-blue-600 to-blue-700',
    hover: 'from-blue-500 to-blue-600',
    shadow: 'shadow-blue-500/25',
    label: 'Facebook',
  },
  {
    name: 'X',
    icon: RiTwitterXLine,
    color: '#000000',
    shareUrl: (url, title) =>
      `https://x.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    gradient: 'from-gray-800 to-black',
    hover: 'from-gray-700 to-gray-900',
    shadow: 'shadow-gray-500/25',
    label: 'X',
  },
  {
    name: 'Threads',
    icon: SiThreads,
    color: '#000000',
    shareUrl: (url, title) => `https://www.threads.net/intent/post?text=${encodeURIComponent(title + ' ' + url)}`,
    gradient: 'from-gray-900 to-black',
    hover: 'from-gray-800 to-gray-900',
    shadow: 'shadow-gray-500/25',
    label: 'Threads',
  },
  {
    name: 'Instagram',
    icon: FaInstagram,
    color: '#E4405F',
    shareUrl: (url, title) =>
      `https://www.instagram.com/create/story/?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    gradient: 'from-pink-500 via-red-500 to-yellow-500',
    hover: 'from-pink-400 via-red-400 to-yellow-400',
    shadow: 'shadow-pink-500/25',
    label: 'Instagram',
  },
  {
    name: 'WhatsApp',
    icon: FaWhatsapp,
    color: '#25d366',
    shareUrl: (url, title) => `https://wa.me/?text=${encodeURIComponent(title + '\n\n' + url)}`,
    gradient: 'from-green-500 to-green-600',
    hover: 'from-green-400 to-green-500',
    shadow: 'shadow-green-500/25',
    label: 'WhatsApp',
  },
  {
    name: 'Telegram',
    icon: FaTelegramPlane,
    color: '#0088cc',
    shareUrl: (url, title) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    gradient: 'from-blue-500 to-blue-600',
    hover: 'from-blue-400 to-blue-500',
    shadow: 'shadow-blue-500/25',
    label: 'Telegram',
  },
  {
    name: 'LinkedIn',
    icon: FaLinkedinIn,
    color: '#0077b5',
    shareUrl: (url, title) =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
    gradient: 'from-blue-700 to-blue-800',
    hover: 'from-blue-600 to-blue-700',
    shadow: 'shadow-blue-500/25',
    label: 'LinkedIn',
  },
  {
    name: 'TikTok',
    icon: FaTiktok,
    color: '#000000',
    shareUrl: (url, title) =>
      `https://www.tiktok.com/share?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    gradient: 'from-black via-red-500 to-blue-500',
    hover: 'from-gray-900 via-red-400 to-blue-400',
    shadow: 'shadow-red-500/25',
    label: 'TikTok',
  },
  {
    name: 'YouTube',
    icon: FaYoutube,
    color: '#FF0000',
    shareUrl: (url) => `https://www.youtube.com/share?url=${encodeURIComponent(url)}`,
    gradient: 'from-red-600 to-red-700',
    hover: 'from-red-500 to-red-600',
    shadow: 'shadow-red-500/25',
    label: 'YouTube',
  },
  {
    name: 'Email',
    icon: FaEnvelope,
    color: '#ea4335',
    shareUrl: (url, title) =>
      `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(title + '\n\n' + url)}`,
    gradient: 'from-red-500 to-red-600',
    hover: 'from-red-400 to-red-500',
    shadow: 'shadow-red-500/25',
    label: 'Email',
  },
]

export const ShareModal = ({ isOpen, onClose, shareUrl, title = '', description = '' }) => {
  const [copied, setCopied] = useState(false)

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      notifyToast('success', 'Link copied!')
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy: ', err)
      notifyToast('error', 'Failed to copy')
    }
  }

  const handlePlatformShare = (platform) => {
    const url = platform.shareUrl(shareUrl, title)
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: shareUrl,
        })
      } catch (err) {
        console.error('Native sharing failed:', err)
      }
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-max">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 size={20} />
            Share this article
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Social Media Platforms Grid */}
          <div className="grid grid-cols-5 gap-3">
            {SOCIAL_MEDIA_PLATFORMS.map((platform) => {
              const IconComponent = platform.icon
              return (
                <Button
                  key={platform.name}
                  variant="ghost"
                  className={`group relative flex h-20 flex-col items-center gap-2 rounded-xl border-0 bg-gradient-to-br ${platform.gradient} p-3 shadow-lg ${platform.shadow} transition-all duration-300 
                     hover:scale-102 hover:bg-gradient-to-br hover:${platform.hover}`}
                  onClick={() => handlePlatformShare(platform)}
                >
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <IconComponent size={16} className="relative text-white" />
                  <span className="relative text-xs font-medium text-white/90 transition-colors group-hover:text-white">
                    {platform.label}
                  </span>
                </Button>
              )
            })}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-4 border-t">
            <Button variant="outline" className="w-full justify-center gap-2 bg-transparent" onClick={handleCopyLink}>
              {copied ? (
                <>
                  <Check size={16} className="text-green-600" />
                  <span className="text-green-600">Copied!</span>
                </>
              ) : (
                <>
                  <Copy size={16} />
                  <span>Copy Link</span>
                </>
              )}
            </Button>

            {navigator.share && (
              <Button className="w-full justify-center gap-2" onClick={handleNativeShare}>
                <Share2 size={16} />
                <span>More Options</span>
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
